import { Component, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta } from '@angular/platform-browser';

import { combineLatest, switchMap, tap } from 'rxjs';

import { SpinnerComponent } from '@components/spinner';
import { NotFoundPageComponent, handleSsrPageErrors } from '@features/errors';
import { PageViewService } from '@features/analytics';

import { CustomPagesService } from '../../services';
import { ContentBlockDirective } from '../../components';

@Component({
  selector: 'app-blog-post',
  templateUrl: './blog-post.component.html',
  imports: [CommonModule, RouterModule, ContentBlockDirective, SpinnerComponent, NotFoundPageComponent],
})
export class BlogPostComponent {
  #service = inject(CustomPagesService);
  #meta = inject(Meta);
  #pageView = inject(PageViewService);

  section = input.required<'blog' | 'news'>();
  slug = input.required<string>();

  protected loaded = signal(false);
  protected notFound = signal(false);

  protected page = toSignal(
    combineLatest([toObservable(this.section), toObservable(this.slug)]).pipe(
      tap(() => {
        this.loaded.set(false);
        this.notFound.set(false);
      }),
      switchMap(([section, slug]) => {
        const fetch = section === 'blog'
          ? this.#service.blogPost(slug)
          : this.#service.newsPost(slug);

        return fetch.pipe(
          tap((post) => {
            const { meta } = post;
            if (meta.author) this.#meta.updateTag({ content: meta.author, name: 'author' });
            if (meta.description) this.#meta.updateTag({ content: meta.description, name: 'description' });
            if (meta.og_description) this.#meta.updateTag({ content: meta.og_description, property: 'og:description' });
            if (meta.og_image) this.#meta.updateTag({ content: meta.og_image, property: 'og:image' });
            if (meta.og_title) this.#meta.updateTag({ content: meta.og_title, property: 'og:title' });
            if (meta.twitter_card) this.#meta.updateTag({ content: meta.twitter_card, name: 'twitter:card' });
            if (meta.twitter_image) this.#meta.updateTag({ content: meta.twitter_image, name: 'twitter:image' });
            if (meta.twitter_description) this.#meta.updateTag({ content: meta.twitter_description, name: 'twitter:description' });
            if (meta.twitter_site) this.#meta.updateTag({ content: meta.twitter_site, name: 'twitter:site' });
            if (meta.twitter_title) this.#meta.updateTag({ content: meta.twitter_title, name: 'twitter:title' });
            if (meta.twitter_url) this.#meta.updateTag({ content: meta.twitter_url, name: 'twitter:url' });

            this.#pageView.enrich({
              content_category: this.section(),
              article_title: post.title || undefined,
              article_slug: post.slug || this.slug(),
            });
          }),
          handleSsrPageErrors({
            onNotFound: () => this.notFound.set(true),
            onSettled: () => this.loaded.set(true),
          }),
        );
      }),
    ),
  );
}
